import { Button, Form } from '@heroui/react'
import { APP_STORE_CONFIG, type User, validateEmail, validateRequired } from '@qse/constants'
import { HeroInput, LogoIcon } from '@qse/ui'
import {
  detectCurrentApp,
  getAdminRegisterUrl,
  getAppStoreUrl,
  getDefaultRoute,
  getPlayStoreUrl,
} from '@qse/utils'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { appStoreBadge, googlePlayBadge } from '../assets'
import { useLogin } from '../logic'

interface LoginScreenProps {
  onSuccess?: (response: User) => void
  onTOTPSetupRequired?: (totpSetup: unknown) => void
  onTOTPCodeRequired?: () => void
  isDashboard?: boolean
}

const Login: React.FC<LoginScreenProps> = ({ isDashboard = false }) => {
  const navigate = useNavigate()
  const { handleLogin } = useLogin()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const reason = localStorage.getItem('logout_reason')

    if (reason) {
      toast.error(reason, { duration: 8000 })
      localStorage.removeItem('logout_reason')
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    const formData = new FormData(e.target as HTMLFormElement)
    const values = {
      email: (formData.get('email') as string).toLowerCase().trim(),
      password: formData.get('password') as string,
    }

    await handleLogin(values, {
      setSubmitting: setIsLoading,
      onSuccess: user => {
        // Role-based navigation using centralized logic
        const { appType } = detectCurrentApp()
        const defaultRoute = getDefaultRoute(user.userRole, appType)

        navigate(defaultRoute)
      },
    })
  }

  return (
    <div className="h-full">
      <div className="sticky top-0 flex lg:justify-end justify-between w-full z-50 px-4 dark:bg-[#000] bg-white py-4">
        <div className="lg:hidden block">
          <LogoIcon className="text-black dark:text-white" />
        </div>
        {!isDashboard ? (
          <Button
            as={Link}
            className="w-[193px] font-medium"
            color="primary"
            radius="sm"
            to="/auth/register"
          >
            Create an Account
          </Button>
        ) : (
          <Button
            className="w-[193px] font-medium"
            color="primary"
            radius="sm"
            onPress={() => {
              window.location.href = getAdminRegisterUrl()
            }}
          >
            Create an Account
          </Button>
        )}
      </div>
      <div className="flex items-center h-[calc(100vh-72px)] w-full md:pl-[50px] xl:pl-[110px] lg:max-w-full max-w-[440px] mx-auto">
        <div className="lg:w-[440px] w-full p-8 lg:p-0 flex flex-col gap-11">
          <div className="flex flex-col gap-2">
            <h1 className="text-[28px] font-medium">Welcome to the QSE GROUP Platform!</h1>
            <p className=" text-[15px]">Please enter your credentials below:</p>
          </div>
          <Form className="w-full" validationBehavior="native" onSubmit={handleSubmit}>
            <div className="w-full">
              <div className="flex flex-col gap-[30px]">
                <HeroInput
                  isRequired
                  control="email"
                  label="Email"
                  name="email"
                  placeholder="Email"
                  radius="sm"
                  size="md"
                  validate={validateEmail}
                  variant="bordered"
                />
                <HeroInput
                  isRequired
                  control="password"
                  label="Password"
                  name="password"
                  placeholder="Enter Password"
                  radius="sm"
                  size="md"
                  validate={validateRequired('Password')}
                  variant="bordered"
                />
              </div>
              <div className="flex flex-col gap-[30px]">
                <div className="flex justify-end mt-[18px]">
                  <Link className="text-sm hover:underline font-medium" to="/auth/forgot-password">
                    Forgot Password
                  </Link>
                </div>
                <span className="text-xs font-light">
                  By logging in, you accept and agree to the{' '}
                  <Link
                    className="underline "
                    target="_blank"
                    to={'https://qse.group/terms-conditions/'}
                  >
                    <span>Terms </span> and <span>Conditions</span>.
                  </Link>
                </span>
              </div>
              <Button
                className="w-full mt-6 font-medium"
                color="primary"
                isLoading={isLoading}
                radius="sm"
                type="submit"
              >
                Login
              </Button>
            </div>
          </Form>
          <div className="flex flex-col gap-5 pt-10 border-t border-gray-200 dark:border-white/10">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Get the Mobile App
            </h2>
            <div className="flex flex-wrap items-start gap-3 sm:items-center sm:gap-4">
              <a
                aria-label="Download on the App Store"
                className="inline-flex items-center justify-center rounded-md"
                href={getAppStoreUrl(APP_STORE_CONFIG.APPLE.APP_ID, APP_STORE_CONFIG.APPLE.WEB_URL)}
                rel="noopener noreferrer"
                target="_blank"
              >
                <img
                  alt="Download on the App Store"
                  className="h-10 w-auto sm:h-11 lg:h-12"
                  loading="lazy"
                  src={appStoreBadge}
                />
              </a>
              <a
                aria-label="Get it on Google Play"
                className="inline-flex items-center justify-center rounded-md"
                rel="noopener noreferrer"
                target="_blank"
                href={getPlayStoreUrl(
                  APP_STORE_CONFIG.GOOGLE_PLAY.PACKAGE_ID,
                  APP_STORE_CONFIG.GOOGLE_PLAY.WEB_URL,
                )}
              >
                <img
                  alt="Get it on Google Play"
                  className="h-10 w-auto sm:h-11 lg:h-12"
                  loading="lazy"
                  src={googlePlayBadge}
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
